import { safeText } from "../shared/ui/ui-helpers";

/* ---------- Types ---------- */
export interface ClientRecord {
  carrierName: string;
  dbaName: string;
  recordType: string;
  hasMapping: boolean;
}

export interface ApiResponse {
  status: number;
  message: string;
  data: Record<string, ClientRecord>;
}

/* ---------- Shared client data (loaded once, shared across controllers) ---------- */
let FULL_CLIENT_MAP: Record<string, ClientRecord> = {};
let _loadClientMapPromise: Promise<void> | null = null;

/* ---------- Client Data Utilities ---------- */
export const toClientLabel = (clientName: string, rec: ClientRecord) =>
  (rec.dbaName = safeText(rec.dbaName)) ? `${clientName} (${rec.dbaName})` : clientName;

export const getUniqueCarriers = (clientMap: Record<string, ClientRecord>, clientNames: string[]) =>
  Array.from(
    clientNames.reduce((s, n) => {
      const c = safeText(clientMap[n]?.carrierName);
      if (c) s.add(c);
      return s;
    }, new Set<string>())
  ).sort((a, b) => a.localeCompare(b));

export const filterClientNamesByQuery = (clientMap: Record<string, ClientRecord>, query: string) => {
  const q = safeText(query).toLowerCase();
  const all = Object.keys(clientMap);
  if (!q) return all.sort((a, b) => a.localeCompare(b));
  return all
    .filter((name) => {
      const rec = clientMap[name];
      const dba = safeText(rec?.dbaName).toLowerCase();
      const n = name.toLowerCase();
      return n.includes(q) || dba.includes(q);
    })
    .sort((a, b) => a.localeCompare(b));
};

export const filterClientNamesByQueryAndMapping = (clientMap: Record<string, ClientRecord>, query: string, hasMapping: boolean) => {
  const q = safeText(query).toLowerCase();
  const all = Object.keys(clientMap);
  return all
    .filter((name) => {
      const rec = clientMap[name];
      if (rec?.hasMapping !== hasMapping) return false;
      if (!q) return true;
      const dba = safeText(rec?.dbaName).toLowerCase();
      const n = name.toLowerCase();
      return n.includes(q) || dba.includes(q);
    })
    .sort((a, b) => a.localeCompare(b));
};

/* ---------- Load client map once ---------- */
export async function loadClientMapOnce(clientListDataCallFn: () => ApiResponse | Promise<ApiResponse>) {
  if (Object.keys(FULL_CLIENT_MAP).length) return;
  _loadClientMapPromise ??= (async () => {
    const apiResult = await Promise.resolve(clientListDataCallFn());
    if (!apiResult || apiResult.status !== 1 || !apiResult.data) {
      throw new Error(apiResult?.message || "Failed to load client list.");
    }
    FULL_CLIENT_MAP = apiResult.data;
  })();
  await _loadClientMapPromise;
}

export function getClientMap(): Record<string, ClientRecord> {
  return FULL_CLIENT_MAP;
}

export function clearClientMap(): void {
  FULL_CLIENT_MAP = {};
  _loadClientMapPromise = null;
}
